import { isDate } from "date-fns";
import { isPlainObject } from "es-toolkit";

export function parseToHour (value: string): number | undefined {
  const parsed: number = Number(value);

  if (parsed >= 0 && parsed <= 23) return parsed;

  return undefined;
}
export function parseToMinute (value: string): number | undefined {
  const parsed: number = Number(value);

  if (parsed >= 0 && parsed <= 59) return parsed;

  return undefined;
}
export function isValidMinute (value: unknown): value is number {
  return typeof value === 'number' && value >= 0 && value <= 59;
}
export function parseToSecond (value: string): number | undefined {
  const parsed: number = Number(value);

  if (parsed >= 0 && parsed <= 59) return parsed;

  return undefined;
}
export function isValidSecond (value: unknown): value is number {
  return typeof value === 'number' && value >= 0 && value <= 59;
}
export function parseToMillisecond (value: string): number | undefined {
  const parsed: number = Number(value);

  if (parsed >= 0 && parsed <= 999) return parsed;

  return undefined;
}
export function isValidMillisecond (value: unknown): value is number {
  return typeof value === 'number' && value >= 0 && value <= 999;
}
export const TIME_SEPARATION_CHAR: string = ":";
export type TimeFormat = "HH:mm"
  | "HH:mm:ss"
  | "HH:mm:ss.SSS";
/**
 * TODO: Keep?
 * PACKAGE_CANDIDATE
 */
export class Time {
  public readonly hour: number;

  public readonly minute: number;

  public readonly second?: number | undefined;

  public readonly millisecond?: number | undefined;

  private constructor (hour: number, minute: number, second?: number, millisecond?: number) {
    this.hour = hour;
    this.minute = minute;
    this.second = second;
    this.millisecond = millisecond;
  }

  /**
   * Returns the time formatted as an ISO-8601 time string.
   *
   * Supported patterns:
   *  - `"HH:mm"`            → hours and minutes
   *  - `"HH:mm:ss"`         → hours, minutes, seconds
   *  - `"HH:mm:ss.SSS"`     → hours, minutes, seconds, milliseconds
   *
   * All fields are zero-padded to their required widths.
   * Missing optional parts (`second`, `millisecond`) are padded or defaulted as needed.
   *
   * @param pattern The desired ISO-8601 compatible output format.
   *                Defaults to `"HH:mm"`.
   * @returns Formatted time string in the selected pattern.
   */
  public toIsoTimeString (pattern: TimeFormat = "HH:mm"): string {
    switch (pattern) {
      case "HH:mm": {
        return `${ this.hour.toString().padStart(2, "0") }${ TIME_SEPARATION_CHAR }${ this.minute.toString().padStart(2, "0") }`;
      }

      case "HH:mm:ss": {
        return `${ this.hour.toString().padStart(2, "0") }${ TIME_SEPARATION_CHAR }${ this.minute.toString().padStart(2, "0") }${ TIME_SEPARATION_CHAR }${ this.second?.toString().padStart(2, "0") }`;
      }

      case "HH:mm:ss.SSS": {
        return `${ this.hour.toString().padStart(2, "0") }${ TIME_SEPARATION_CHAR }${ this.minute.toString().padStart(2, "0") }${ TIME_SEPARATION_CHAR }${ this.second?.toString().padStart(2, "0") }.${ this.millisecond?.toString() }`;
      }
    }
  }

  public static fromDate (date: Date): Time {
    return new Time(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
  }

  public static formString (string: string): Time | undefined {
    const splitString: string[] = string.split(TIME_SEPARATION_CHAR);

    // Only valid if hours and minutes are given
    if (splitString.length < 2) return undefined;

    // eslint-disable-next-line @tseslint/typedef
    const [ hour = undefined, minute = undefined, second = undefined, millisecond = undefined ] = splitString;
    const parsedHour: number | undefined = hour ? parseToHour(hour) : undefined;
    const parsedMinute: number | undefined = minute ? parseToMinute(minute) : undefined;
    const parsedSecond: number | undefined = second ? parseToSecond(second) : undefined;
    const parsedMillisecond: number | undefined = millisecond ? parseToMillisecond(millisecond) : undefined;


    if (parsedHour === undefined || parsedMinute === undefined) return;

    return new Time(parsedHour, parsedMinute, parsedSecond, parsedMillisecond);
  }
}
export function isTime (value: unknown): value is Time {
  return value instanceof Time;
}
// eslint-disable-next-line @tseslint/typedef
export const DATE_TYPES = {
  timespan: "timespan",
  single: "single"
} as const;
export type DateType = typeof DATE_TYPES[keyof typeof DATE_TYPES];
// eslint-disable-next-line @tseslint/typedef
export const TIME_TYPES = {
  wholeDay: "whole-day",
  timespan: "timespan"
} as const;
export type TimeType = typeof TIME_TYPES[keyof typeof TIME_TYPES];
export interface WholeDayTime {
  type: typeof TIME_TYPES.wholeDay;
}
export interface Timespan {
  type: typeof TIME_TYPES.timespan;
  startTime: Time;
  endTime: Time;
}
export interface SingleDate {
  type: typeof DATE_TYPES.single;
  date: Date;
}
export interface DateTimespan {
  type: typeof DATE_TYPES.timespan;
  startDate: Date;
  endDate: Date;
}
export type DatePeriod = SingleDate | DateTimespan;
export type TimePeriod = WholeDayTime | Timespan;
export function isDatePeriod (value: unknown): value is DatePeriod {
  if (isPlainObject(value)) {
    const maybeDatePeriod: DatePeriod = value as DatePeriod;

    if (maybeDatePeriod.type === DATE_TYPES.single && isDate(maybeDatePeriod.date)) return true;

    if (maybeDatePeriod.type === DATE_TYPES.timespan && isDate(maybeDatePeriod.startDate) && isDate(maybeDatePeriod.endDate)) return true;
  }

  return false;
}
export function isTimePeriod (value: unknown): value is TimePeriod {
  if (isPlainObject(value)) {
    const maybeTimePeriod: TimePeriod = value as TimePeriod;

    if (maybeTimePeriod.type === TIME_TYPES.wholeDay) return true;

    // eslint-disable-next-line @tseslint/no-unnecessary-condition
    if (maybeTimePeriod.type === TIME_TYPES.timespan && isTime(maybeTimePeriod.startTime) && isTime(maybeTimePeriod.endTime)) return true;
  }

  return false;
}
